import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Box, FormControlLabel, Radio, RadioGroup, TextField,
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import FormDialog from '../../../components/Dialog/FormDialog';
import Typography from '../../../components/Typography/CustomTypography';
import Button from '../../../components/Button/CustomButton';
import UploadButton from '../../../components/Button/UploadButton';
import {
    MEDIA_TYPE,
    SERVER_CHAR_LIMIT,
    BUSINESS_CONTACT,
    BUSINESS_CONTACT_VIEW,
    OUTSIDE_LINK,
    DISPLAY_BUSINESS_CARD,
    BOOTH_TYPE,
} from '../../../common/Constants';
import CleanableInput from '../../../components/Input/CleanableInput';
import ReactPlayer from '../../../components/ReactPlayer/ReactPlayer';
import listExhibitionStyle from '../../../assets/jss/pages/listExhibitionStyle';
import SnackBar from '../../../components/Snackbar/Snackbar';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Validation from '../../../common/Validation';
import ObjectHelper from '../../../common/helper/ObjectHelper';
import { UploadIcon } from '../../../components/Icons/Icon';
import InputHasUnit from '../../SeminarManagementOrganizer/inputHasUnit';

const useStyles = makeStyles(listExhibitionStyle);

const PRODUCT_DIALOG_MODE = {
    READ: 1,
    UPDATE: 2,
};

const DialogProductExhibition = (props) => {
    const {
        isOpen,
        handleClose,
        fetchGetDetailProduct,
        fetchClearGetDetailProduct,
        fetchEditProduct,
        productData,
        productId,
        mode,
        onClickChangeToEditDialog,
        editErrors,
        fetchClearEditProduct,
        token,
        eventInfo,
        settingsDisplayInfo,
        companyDetail,
        fetchList,
    } = props;
    const intl = useIntl();
    const classes = useStyles();
    const urlParams = useParams();
    const [params, setParams] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const [mediaTypeState, setMediaTypeState] = useState(MEDIA_TYPE.IMAGE);
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);
    const [msgSnackBar, setMsgSnackBar] = useState('');
    const [error, setError] = useState('');
    const [nameFile, setNameFile] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchGetDetailProduct({
                company_id: urlParams.idCompany,
                product_id: productId,
            });
        }
        if (!isOpen) {
            fetchClearGetDetailProduct();
            setParams({});
            setValidateResults({});
            setError('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!_.isEmpty(productData)) {
            setParams({
                ...params,
                mediaType: productData.media_type,
                productName: productData.name,
                productNameEN: productData.name_en,
                productDescription: productData.description,
                productDescriptionEN: productData.description_en,
                purchaseLink: productData.purchase_link,
                businessContact: productData.business_contact,
                documentRequestReception: productData.document_link,
                survey: productData.survey_link,
                hasOutsideLink: productData.has_outside_link,
                externalLink2: productData.outside2_link,
                externalLink3: productData.outside3_link,
                mediaFileSource: productData.media,
                documentFileSource: productData.document,
                isChangeMedia: false,
                isChangeMediaType: false,
                media_status: productData.media_status,
                business_card: productData.business_card,
                business_card_info: productData.business_card_info,
                price: productData.price,
                available: productData?.available || 0,
            });
            if (mode === PRODUCT_DIALOG_MODE.UPDATE || mode === PRODUCT_DIALOG_MODE.READ) {
                setMediaTypeState(productData.media_type);
            }
        }
    }, [productData, mode]);

    useEffect(() => {
        if (!_.isEmpty(editErrors)) {
            if (editErrors.code === 'BAD_REQUEST') {
                setValidateResults({
                    ...validateResults,
                    ...editErrors,
                });
                if (editErrors.message) setMsgSnackBar(editErrors.message.message);
                if (editErrors.business_contact) setMsgSnackBar(editErrors.business_contact.message);
                if (editErrors.document) {
                    setMsgSnackBar(editErrors.document.message);
                    setError(editErrors.document.message);
                }
                setIsOpenSnackBar(true);
                fetchClearEditProduct();
                handleClose();
                fetchList();
            }
        }
    }, [editErrors]);

    const actionsButton = () => (
        <Box style={{ margin: 'auto' }}>
            <Button
                autoFocus
                onClick={mode === PRODUCT_DIALOG_MODE.READ ? onClickChangeToEditDialog : handleEditExhibition}
                size="md"
                color="success"
                style={{ width: '200px', fontSize: '20px', left: ' 20%' }}
            >
                {mode === PRODUCT_DIALOG_MODE.READ ? '編集する' : '保存する'}
            </Button>

            <Button
                autoFocus
                onClick={handleClose}
                size="md"
                color="danger"
                style={{ width: '150px', marginRight: '10px', left: ' 80%' }}
            >
                キャンセル
            </Button>
        </Box>
    );

    const handleSetParams = useCallback((field, valueChange) => {
        setParams((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const handleUpload = (event) => {
        event.preventDefault();
        const file = event.target.files[0];
        const sizeLimit = 15;
        if (file) {
            if ((file.size / (1024 * 1024)) > sizeLimit) {
                const sizeLimitDisplay = sizeLimit >= 1024 ? `${sizeLimit / 1024}GB` : `${sizeLimit}MB`;
                setError(intl.formatMessage({ id: 'validation.maxSize' }, { maxSize: sizeLimitDisplay }));
                return;
            }
            setNameFile(file.name);
            handleSetParams('document', file);
            setError('');
            event.target.value = ''; // clear file
        }
    };

    useEffect(() => {
        setNameFile(getFileNameFromUrl(params.documentFileSource));
    }, [params.documentFileSource]);

    const handleEditExhibition = () => {
        if (validate() && validateMedia()) {
            let payload = {
                ...params,
                company_id: urlParams.idCompany,
                product_id: productId,
                business_contact: params.businessContact ? BUSINESS_CONTACT.show.mainValue : BUSINESS_CONTACT.hide.mainValue,
                has_outside_link: params.hasOutsideLink ? '1' : '0',
            };

            if (!params.isChangeMedia && !params.isChangeMediaType) {
                payload = ObjectHelper.removePropertyByKey('mediaType')(payload);
            }
            if (companyDetail.organizerBooth !== BOOTH_TYPE.GRAND) {
                payload = ObjectHelper.removePropertyByKey('available')(payload);
            }
            fetchEditProduct(payload);
        }
    };

    const validateMedia = () => {
        if (params.tempMediaType === params.mediaType) {
            return true;
        }
        if (!params.isChangeMedia && params.isChangeMediaType) {
            if (params.mediaType === 0) {
                setMsgSnackBar('画像（jpe、jpg、jpeg、png）を選択してください。');
            }
            if (params.mediaType === 1) {
                setMsgSnackBar('音声（mp3、aac、flac）を選択してください。');
            }
            if (params.mediaType === 2) {
                setMsgSnackBar('動画（mp4、mov、webm、mkv、wmv、flv）を選択してください。');
            }
            setIsOpenSnackBar(true);
        } else {
            return true;
        }
        return false;
    };

    const validate = () => {
        const {
            media, mediaFileSource, productName, productDescription, purchaseLink, businessContact, documentRequestReception,
            survey, externalLink2, externalLink3, price, productNameEN, productDescriptionEN,
        } = params;

        const validateRules = {
            productImage: [() => !mediaFileSource && Validation.isNotEmpty(media)],
            productName: [
                () => Validation.isNotEmpty(productName),
                () => Validation.validateMaxLength(productName, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof productName),
            ],
            productDescription: [
                () => Validation.isNotEmpty(productDescription),
                () => Validation.validateMaxLength(productDescription, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof productDescription),
            ],
            purchaseLink: [
                () => Validation.validateMaxLength(purchaseLink, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof purchaseLink),
            ],
            businessContact: [
                () => Validation.validateMaxLength(businessContact, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof businessContact),
            ],
            documentRequestReception: [
                () => Validation.validateMaxLength(documentRequestReception, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof documentRequestReception),
            ],
            survey: [
                () => Validation.isValidUrl(survey),
                () => Validation.validateMaxLength(survey, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof survey),
            ],
            externalLink2: [
                () => Validation.isValidUrl(externalLink2),
                () => Validation.validateMaxLength(externalLink2, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof externalLink2),
            ],
            externalLink3: [
                () => Validation.isValidUrl(externalLink3),
                () => Validation.validateMaxLength(externalLink3, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof externalLink3),
            ],
            price: [
                // () => Validation.noCostNotFloat(price),
                () => Validation.isInt(price),
            ],
        };
        if (!_.isEmpty(eventInfo.languages)) {
            validateRules.productNameEN = [
                () => Validation.isNotEmpty(productNameEN),
                () => Validation.validateLatin(productNameEN),
                () => Validation.validateMaxLength(productNameEN, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof productNameEN),
            ];
            validateRules.productDescriptionEN = [
                () => Validation.isNotEmpty(productDescriptionEN),
                () => Validation.validateLatin(productDescriptionEN),
                () => Validation.validateMaxLength(productDescriptionEN, SERVER_CHAR_LIMIT.TEXT_FIELD, typeof productDescriptionEN),
            ];
        }
        if (companyDetail.organizerBooth === BOOTH_TYPE.GRAND) {
            validateRules.available = [
                () => Validation.isNotEmpty(params.available),
            ];
        }

        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const getFileNameFromUrl = (value) => {
        if (!value) return '';
        const lastSlashIndex = value.lastIndexOf('/');
        if (lastSlashIndex === -1) return value;
        return value.slice(lastSlashIndex + 1);
    };

    const mediaTypeToUploadType = (value) => {
        switch (value) {
            case MEDIA_TYPE.VIDEO:
                return 'video';
            case MEDIA_TYPE.AUDIO:
                return 'audio';
            default:
                return 'image';
        }
    };

    const handleChangeTotal = (e) => {
        if (/^\d*$/.test(e.target.value)) {
            const value = e.target.value ? parseInt(e.target.value, 10) : '';
            handleSetParams('available', value);
        }
    };

    const watchPriceChange = (e) => {
        if (!/^\d*$/.test(e.key)) e.preventDefault();
    };

    const mediaTypeSizeLimit = (value) => {
        switch (value) {
            case MEDIA_TYPE.VIDEO:
            case MEDIA_TYPE.AUDIO:
                return 100;
            case MEDIA_TYPE.IMAGE:
                return 15;
            default:
                return undefined;
        }
    };

    const handleChangeMediaType = (e) => {
        setMediaTypeState(parseInt(e.target.value, 10));
        if (parseInt(e.target.value, 10) !== productData.media_type) {
            setParams({
                ...params,
                mediaType: parseInt(e.target.value, 10),
                isChangeMediaType: true,
                isChangeMedia: false,
            });
        }
        if (parseInt(e.target.value, 10) === productData.media_type && params.media) {
            setParams({
                ...params,
                mediaType: parseInt(e.target.value, 10),
                isChangeMediaType: true,
                isChangeMedia: false,
            });
        }
        if (parseInt(e.target.value, 10) === productData.media_type && !params.media) {
            setParams({
                ...params,
                mediaType: parseInt(e.target.value, 10),
                isChangeMediaType: false,
                isChangeMedia: false,
            });
        }
    };

    const handleChangeBusinessContact = (event) => {
        setParams({
            ...params,
            businessContact: event.target.value === BUSINESS_CONTACT.show.extraValue,
        });
    };

    const handleChangeOutsideLink = (e) => {
        setParams({
            ...params,
            hasOutsideLink: e.target.value === 'true',
        });
    };

    const calculateLimitDurationByType = () => {
        if (mediaTypeState === MEDIA_TYPE.VIDEO) {
            return eventInfo.max_vtime_exhibition;
        }

        if (mediaTypeState === MEDIA_TYPE.AUDIO) {
            return eventInfo.max_atime_exhibition;
        }

        return 0;
    };

    const renderProductInfoRow = (label, isRequired, content) => (
        <GridContainer className={classes.spacing}>
            <GridItem xs={12} sm={4} md={3}>
                <Typography required={isRequired} bold>
                    <FormattedMessage id={label} />
                </Typography>
            </GridItem>
            <GridItem xs={12} sm={8} md={9}>
                {content}
            </GridItem>
        </GridContainer>
    );

    const handleChangeMedia = (value) => {
        setParams({
            ...params,
            media: value,
            isChangeMedia: true,
            isChangeMediaType: false,
            tempMediaType: params.mediaType,
        });
    };

    const renderProductImage = () => {
        if (mode === PRODUCT_DIALOG_MODE.READ) {
            if (mediaTypeState === MEDIA_TYPE.IMAGE) {
                return (
                    <img src={params.mediaFileSource} alt="" style={{ maxWidth: '100%', maxHeight: '200px' }} />
                );
            }
            return (
                <ReactPlayer
                    url={params.mediaFileSource && `${params.mediaFileSource}?token=${token}`}
                    mediaType={mediaTypeState}
                    status={params.media_status}
                />
            );
        }
        return (
            <Box mt="-9px">
                <RadioGroup row value={mediaTypeState} onChange={(e) => handleChangeMediaType(e)}>
                    <FormControlLabel
                        classes={{ label: classes.radioButton }}
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: 'image' })}
                        value={MEDIA_TYPE.IMAGE}
                    />
                    <FormControlLabel
                        classes={{ label: classes.radioButton }}
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: 'video' })}
                        value={MEDIA_TYPE.VIDEO}
                    />
                    <FormControlLabel
                        classes={{ label: classes.radioButton }}
                        control={<Radio color="primary" />}
                        label={intl.formatMessage({ id: 'audio' })}
                        value={MEDIA_TYPE.AUDIO}
                    />
                </RadioGroup>
                <UploadButton
                    imagePreview={mediaTypeState === MEDIA_TYPE.IMAGE ? params.mediaFileSource : undefined}
                    namePreview={mediaTypeState !== MEDIA_TYPE.IMAGE
                        ? params.mediaFileSource && params.mediaFileSource.slice(params.mediaFileSource.lastIndexOf('/') + 1)
                        : undefined}
                    type={mediaTypeToUploadType(mediaTypeState)}
                    maxSize={mediaTypeSizeLimit(mediaTypeState)}
                    onChange={(value) => handleChangeMedia(value)}
                    limitDuration={calculateLimitDurationByType()}
                />
                {
                    !!validateResults.productImage && (
                        <Typography
                            color="danger"
                            variant="caption"
                            component="div"
                            classes={{ caption: classes.errorText }}
                        >
                            {intl.formatMessage({ id: validateResults.productImage.message })}
                        </Typography>
                    )
                }
            </Box>
        );
    };

    return (
        <>
            <FormDialog isOpen={isOpen} handleClose={handleClose} title={intl.formatMessage({ id: 'productUpdate.pageTitle' })} actions={actionsButton()}>
                <GridContainer className={classes.spacing}>
                    {renderProductInfoRow('productRegister.productImage', mode !== PRODUCT_DIALOG_MODE.READ, renderProductImage())}
                    {renderProductInfoRow(!_.isEmpty(eventInfo.languages) ? 'productRegister.productNameJP' : 'productRegister.productName', mode !== PRODUCT_DIALOG_MODE.READ,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.productName
                            : (
                                <CleanableInput
                                    defaultValue={params.productName}
                                    isMultiline
                                    onChange={(value) => handleSetParams('productName', value)}
                                    error={!!validateResults.productName}
                                    helperText={validateResults.productName
                                        ? intl.formatMessage({ id: validateResults.productName.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {!_.isEmpty(eventInfo.languages) && renderProductInfoRow('productRegister.productNameEN', mode !== PRODUCT_DIALOG_MODE.READ,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.productNameEN
                            : (
                                <CleanableInput
                                    defaultValue={params.productNameEN}
                                    isMultiline
                                    onChange={(value) => handleSetParams('productNameEN', value)}
                                    error={!!validateResults.productNameEN}
                                    helperText={validateResults.productNameEN
                                        ? intl.formatMessage({ id: validateResults.productNameEN.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {renderProductInfoRow(!_.isEmpty(eventInfo.languages) ? 'productRegister.productDescriptionJP' : 'productRegister.productDescription', mode !== PRODUCT_DIALOG_MODE.READ,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.productDescription
                            : (
                                <CleanableInput
                                    defaultValue={params.productDescription}
                                    isMultiline
                                    onChange={(value) => handleSetParams('productDescription', value)}
                                    error={!!validateResults.productDescription}
                                    helperText={validateResults.productDescription
                                        ? intl.formatMessage({ id: validateResults.productDescription.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {!_.isEmpty(eventInfo.languages) && renderProductInfoRow('productRegister.productDescriptionEN', mode !== PRODUCT_DIALOG_MODE.READ,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.productDescriptionEN
                            : (
                                <CleanableInput
                                    defaultValue={params.productDescriptionEN}
                                    isMultiline
                                    onChange={(value) => handleSetParams('productDescriptionEN', value)}
                                    error={!!validateResults.productDescriptionEN}
                                    helperText={validateResults.productDescriptionEN
                                        ? intl.formatMessage({ id: validateResults.productDescriptionEN.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {companyDetail.organizerBooth === BOOTH_TYPE.GRAND && renderProductInfoRow('productRegister.productAvailableProduct', mode !== PRODUCT_DIALOG_MODE.READ,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? +params.available
                            : (
                                <TextField
                                    variant="outlined"
                                    className={classes.currencyInput}
                                    value={params.available}
                                    onChange={(value) => handleChangeTotal(value)}
                                    onKeyPress={watchPriceChange}
                                    inputProps={{
                                        min: 0,
                                    }}
                                    InputProps={{ classes: { root: `${classes.numberInput}` } }}
                                    error={!!validateResults.available}
                                    helperText={validateResults.available
                                        ? '必須です。入力してください。' : ''}
                                />
                            ))}
                    {settingsDisplayInfo.display_purchase_link && renderProductInfoRow('productRegister.purchaseLink', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.purchaseLink
                            : (
                                <CleanableInput
                                    defaultValue={params.purchaseLink}
                                    onChange={(value) => handleSetParams('purchaseLink', value)}
                                    error={!!validateResults.purchaseLink}
                                    helperText={validateResults.purchaseLink
                                        ? intl.formatMessage({ id: validateResults.purchaseLink.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {companyDetail.organizerBooth === BOOTH_TYPE.GRAND && renderProductInfoRow('price', mode !== PRODUCT_DIALOG_MODE.READ,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? `${intl.formatNumber(params.price)}円`
                            : (
                                <InputHasUnit
                                    defaultValue={params.price}
                                    onChange={(valueInput) => handleSetParams('price', valueInput)}
                                    unit="円"
                                    minValue={0}
                                    error={!!validateResults.price}
                                    helperText={validateResults.price
                                        ? intl.formatMessage(
                                            { id: validateResults.price.message },
                                        ) : ''}
                                    className={classes.numberInput}
                                />
                            ))}
                    {settingsDisplayInfo.display_has_outside_link && renderProductInfoRow('productRegister.externalLink', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? OUTSIDE_LINK[params.hasOutsideLink]
                            : (
                                <RadioGroup
                                    row
                                    value={`${params.hasOutsideLink}`}
                                    onChange={handleChangeOutsideLink}
                                >
                                    <FormControlLabel
                                        classes={{ label: classes.radioButton }}
                                        control={<Radio color="primary" />}
                                        label="利用する"
                                        value="true"
                                    />
                                    <FormControlLabel
                                        classes={{ label: classes.radioButton }}
                                        control={<Radio color="primary" />}
                                        label="利用しない"
                                        value="false"
                                    />
                                </RadioGroup>
                            ))}
                    {settingsDisplayInfo.display_document && renderProductInfoRow('productRegister.productDocumentation', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? (
                                <a href={params.documentFileSource} target="blank">
                                    {getFileNameFromUrl(params.documentFileSource)}
                                </a>
                            )
                            : (
                                <>
                                    <Button size="md" component="label" color="info">
                                        <input
                                            type="file"
                                            style={{ display: 'none' }}
                                            onChange={handleUpload}
                                        />
                                        <UploadIcon />
                                    </Button>
                                    {
                                        error && (
                                            <Typography color="danger" style={{ marginTop: '8px' }}>
                                                {error}
                                            </Typography>
                                        )

                                    }
                                    {nameFile && (
                                        <div style={{ margin: '8px' }}>
                                            <Typography color="info">
                                                {nameFile}
                                            </Typography>
                                        </div>
                                    )}
                                </>
                            ))}

                    {settingsDisplayInfo.display_document_link && renderProductInfoRow('productRegister.documentRequestReception', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.documentRequestReception
                            : (
                                <>
                                    <CleanableInput
                                        defaultValue={params.documentRequestReception}
                                        onChange={(value) => handleSetParams('documentRequestReception', value)}
                                        error={!!validateResults.documentRequestReception}
                                        helperText={validateResults.documentRequestReception
                                            ? intl.formatMessage({ id: validateResults.documentRequestReception.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                    />
                                    <Typography>
                                        ＊御社の資料請求申し込みＵＲＬを入力してください。
                                    </Typography>
                                </>
                            ))}
                    {settingsDisplayInfo.display_survey_link && renderProductInfoRow('productRegister.survey', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.survey
                            : (
                                <CleanableInput
                                    defaultValue={params.survey}
                                    onChange={(value) => handleSetParams('survey', value)}
                                    error={!!validateResults.survey}
                                    helperText={validateResults.survey
                                        ? intl.formatMessage({ id: validateResults.survey.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {settingsDisplayInfo.display_business_contact && renderProductInfoRow('productRegister.businessContactNewText', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? BUSINESS_CONTACT_VIEW[params.businessContact]
                            : (
                                <RadioGroup row value={`${params.businessContact}`} onChange={handleChangeBusinessContact}>
                                    <FormControlLabel
                                        classes={{ label: classes.radioButton }}
                                        control={<Radio color="primary" />}
                                        label={BUSINESS_CONTACT.show.text}
                                        value={BUSINESS_CONTACT.show.extraValue}
                                    />
                                    <FormControlLabel
                                        classes={{ label: classes.radioButton }}
                                        control={<Radio color="primary" />}
                                        label={BUSINESS_CONTACT.hide.text}
                                        value={BUSINESS_CONTACT.hide.extraValue}
                                    />
                                </RadioGroup>
                            ))}
                    {settingsDisplayInfo.display_outside2_link && renderProductInfoRow('productRegister.externalLink2', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.externalLink2
                            : (
                                <CleanableInput
                                    defaultValue={params.externalLink2}
                                    onChange={(value) => handleSetParams('externalLink2', value)}
                                    error={!!validateResults.externalLink2}
                                    helperText={validateResults.externalLink2
                                        ? intl.formatMessage({ id: validateResults.externalLink2.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                />
                            ))}
                    {settingsDisplayInfo.display_outside3_link && renderProductInfoRow('productRegister.externalLink3', false,
                        mode === PRODUCT_DIALOG_MODE.READ
                            ? params.externalLink3
                            : (
                                <>
                                    <CleanableInput
                                        defaultValue={params.externalLink3}
                                        onChange={(value) => handleSetParams('externalLink3', value)}
                                        error={!!validateResults.externalLink3}
                                        helperText={validateResults.externalLink3
                                            ? intl.formatMessage({ id: validateResults.externalLink3.message }, { maxLength: SERVER_CHAR_LIMIT.TEXT_FIELD }) : ''}
                                    />
                                    <Typography>
                                        ＊正しいＵＲＬを入力してください。
                                    </Typography>
                                </>
                            ))}

                    {settingsDisplayInfo.display_business_card && renderProductInfoRow('productRegister.businessCard', false,
                        DISPLAY_BUSINESS_CARD[productData.business_card])}

                    {/* business card exchange */}
                    {(productData.business_card && settingsDisplayInfo.display_business_card) && (
                        <>
                            {renderProductInfoRow('department', false, productData.business_card_info.department)}
                            {renderProductInfoRow('position', false, productData.business_card_info.position)}
                            {renderProductInfoRow('name', false, `${productData.business_card_info.first_name} ${productData.business_card_info.last_name}`)}
                            {renderProductInfoRow('zipCode', false, productData.business_card_info.zip_code)}
                            {renderProductInfoRow('address', false, productData.business_card_info.address)}
                            {renderProductInfoRow('phoneNumber', false, productData.business_card_info.phone_number)}
                            {renderProductInfoRow('faxNumber', false, productData.business_card_info.fax_number)}
                            {renderProductInfoRow('emailPublic', false, productData.business_card_info.email_public)}
                            {renderProductInfoRow('emailNotifications', false, productData.business_card_info.email_notifications)}
                        </>
                    )}

                </GridContainer>
            </FormDialog>

            <SnackBar
                open={isOpenSnackBar}
                onClose={() => setIsOpenSnackBar(false)}
                message={msgSnackBar}
                severity="error"
            />
        </>
    );
};

DialogProductExhibition.defaultProps = {
    isOpen: false,
    productData: {},
    editErrors: {},
    token: '',
    eventInfo: {},
    settingsDisplayInfo: {},
    fetchList: () => {},
};

DialogProductExhibition.propTypes = {
    isOpen: PropTypes.bool,
    handleClose: PropTypes.func.isRequired,
    mode: PropTypes.number.isRequired,
    fetchGetDetailProduct: PropTypes.func.isRequired,
    fetchClearGetDetailProduct: PropTypes.func.isRequired,
    fetchEditProduct: PropTypes.func.isRequired,
    productId: PropTypes.number.isRequired,
    productData: PropTypes.oneOfType([() => null, PropTypes.array]),
    onClickChangeToEditDialog: PropTypes.func.isRequired,
    fetchClearEditProduct: PropTypes.func.isRequired,
    editErrors: PropTypes.oneOfType([() => null, PropTypes.array]),
    token: PropTypes.string,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    settingsDisplayInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    companyDetail: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    fetchList: PropTypes.func,
};

export default DialogProductExhibition;
